import { EditorIcon } from "@/components/environments/EditorIcon";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Switch } from "@/components/Switch";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations } from "@/hooks/use-segment";
import { useListEditors, type PlainEditor } from "@/queries/editor-queries";
import {
    useGetOrganizationPolicies,
    useUpdateOrganizationPolicies,
    type PlainOrganizationPolicies,
} from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import { EditorVersionPolicy } from "gitpod-next-api/gitpod/v1/organization_pb";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/podkit/dropdown/DropDown";

import { ChevronDown, X, Info } from "lucide-react";
import * as semver from "semver";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import { Label } from "@/components/podkit/forms/Label";

type AvailableEditorsProps = {
    editable?: boolean;
};

export const AvailableEditors: FC<AvailableEditorsProps> = ({ editable = true }) => {
    const [isManageEditorsModalOpen, setIsManageEditorsModalOpen] = useState(false);

    const { data: editors, isLoading: isEditorsLoading } = useListEditors();
    const { data: organizationPolicies, isLoading: isOrganizationPoliciesLoading } = useGetOrganizationPolicies();
    const isLoading = isEditorsLoading || isOrganizationPoliciesLoading;

    const availableEditors = useMemo(() => {
        if (!!organizationPolicies?.allowedEditorIds && organizationPolicies?.allowedEditorIds.length === 0) {
            return editors?.editors || [];
        }
        return editors?.editors.filter((editor) => organizationPolicies?.allowedEditorIds.includes(editor.id)) || [];
    }, [editors, organizationPolicies]);

    return (
        <div className="flex flex-col gap-4 rounded-lg border border-border-subtle px-5 py-4">
            <div className="flex items-center justify-between">
                <Heading3>Available editors</Heading3>
                <Button
                    data-testid="manage-editors-button"
                    disabled={!editable}
                    onClick={() => setIsManageEditorsModalOpen(true)}
                    variant="secondary"
                    size="sm"
                    data-tracking-id="manage-editors-available-editors-element"
                >
                    Manage
                </Button>
            </div>
            <SkeletonBlock ready={!isLoading && availableEditors.length > 0}>
                <div className="flex flex-wrap gap-2">
                    {availableEditors.map((editor, index) => {
                        return (
                            <EditorPill
                                key={`${editor.id}-${index}`}
                                editor={editor}
                                isDefault={editor.id === organizationPolicies?.defaultEditorId}
                            />
                        );
                    })}
                </div>
            </SkeletonBlock>
            {isManageEditorsModalOpen && organizationPolicies && (
                <ManageEditorsModal
                    data-testid="manage-editors-modal"
                    onClose={() => setIsManageEditorsModalOpen(false)}
                    editors={editors?.editors || []}
                    policies={organizationPolicies}
                    editable={editable}
                />
            )}
        </div>
    );
};

const EditorPill: FC<{ editor: PlainEditor; isDefault?: boolean; versionRestrictions?: string[] }> = ({
    editor,
    isDefault,
}) => {
    return (
        <div
            className={cn(
                "inline-flex h-9 select-none items-center gap-2 whitespace-nowrap rounded-xl border-0.5 bg-surface-primary p-2 px-4 pb-[6.5px] pt-[5.5px] text-base font-medium",
                isDefault ? "border-border-brand" : "border-border-base",
            )}
        >
            <EditorIcon editor={editor} size="base" />
            <span>{editor.name}</span>
            {isDefault && (
                <span className="rounded-md bg-surface-secondary px-2 py-0.5 text-xs text-content-secondary">
                    default
                </span>
            )}
        </div>
    );
};

type ModalProps = {
    onClose: () => void;
    editors: PlainEditor[];
    policies: PlainOrganizationPolicies;
    editable?: boolean;
};
const ManageEditorsModal: FC<ModalProps> = ({ onClose, editors, policies, editable = true }) => {
    const [selectedEditors, setSelectedEditors] = useState<Record<string, boolean>>(() => {
        if (policies.allowedEditorIds.length > 0) {
            return policies.allowedEditorIds.reduce(
                (acc, id) => {
                    acc[id] = true;
                    return acc;
                },
                {} as Record<string, boolean>,
            );
        }
        return editors.reduce(
            (acc, editor) => {
                acc[editor.id] = true;
                return acc;
            },
            {} as Record<string, boolean>,
        );
    });

    const [defaultEditor, setDefaultEditor] = useState<string>(policies.defaultEditorId || "invalid");

    // State for version restrictions per editor
    const [editorVersionRestrictions, setEditorVersionRestrictions] = useState<Record<string, string[]>>(() => {
        const restrictions: Record<string, string[]> = {};
        if (policies.editorVersionRestrictions) {
            Object.entries(policies.editorVersionRestrictions).forEach(([editorId, policy]) => {
                restrictions[editorId] = policy.allowedVersions || [];
            });
        }
        return restrictions;
    });

    const [isDirty, setIsDirty] = useState(false);

    const isInvalidSelection = useMemo(() => {
        return !Object.entries(selectedEditors).some(([_, checked]) => checked);
    }, [selectedEditors]);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    const handleSave = useCallback(async () => {
        const params: PartialMessage<OrganizationPolicies> = {
            allowedEditorIds: Object.entries(selectedEditors)
                .filter(([_, checked]) => checked)
                .map(([id]) => id),
        };
        if (defaultEditor !== "invalid") {
            params.defaultEditorId = defaultEditor;
        }

        // Build editor version restrictions for all editor
        // Include all editors so backend can distinguish between
        // "no restrictions" (empty allowedVersions) and "field not updated" (key absent)
        const restrictions: Record<string, EditorVersionPolicy> = {};
        editors.forEach((editor) => {
            const versions = editorVersionRestrictions[editor.id] || [];
            restrictions[editor.id] = new EditorVersionPolicy({
                allowedVersions: versions,
            });
        });
        params.editorVersionRestrictions = restrictions;

        try {
            await updateOrganizationPolicies.mutateAsync(params);
            onClose();
        } catch (error) {
            console.error(error);
            toast({
                title: "We couldn't save your changes. Sorry about that! Please retry.",
                description: formatError(error),
            });
        }
    }, [
        selectedEditors,
        defaultEditor,
        editorVersionRestrictions,
        editors,
        updateOrganizationPolicies,
        onClose,
        toast,
    ]);

    const handleEditorSelectionChange = useCallback(
        (editorId: string, checked: boolean) => {
            setSelectedEditors((prev) => ({ ...prev, [editorId]: checked }));
            setIsDirty(true);
            if (!checked && defaultEditor === editorId) {
                const newDefaultEditor = Object.entries(selectedEditors).find(
                    ([id, checked]) => checked && id !== editorId,
                )?.[0];
                setDefaultEditor(newDefaultEditor || "invalid");
            }
            if (checked && defaultEditor === "invalid") {
                setDefaultEditor(editorId);
            }
        },
        [defaultEditor, selectedEditors],
    );

    const handleDefaultEditorChange = useCallback((editorId: string) => {
        setDefaultEditor(editorId);
        setIsDirty(true);
    }, []);

    const handleVersionRestrictionsChange = useCallback((editorId: string, versions: string[]) => {
        setEditorVersionRestrictions((prev) => {
            return {
                ...prev,
                [editorId]: versions.sort((a, b) => {
                    const va = semver.coerce(a);
                    const vb = semver.coerce(b);
                    if (va === null || vb === null) {
                        return b.localeCompare(a);
                    }
                    return semver.compare(vb, va);
                }),
            };
        });
        setIsDirty(true);
    }, []);

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="manage-editors-modal-content"
                data-track-location={TrackLocations.ManageEditorsModal}
            >
                <DialogHeader>
                    <DialogTitle>Available editors</DialogTitle>
                    <DialogDescription>Manage the editors available to your members</DialogDescription>
                </DialogHeader>

                <DialogBody className="overflow-x max-w-full space-y-1">
                    {editors.map((editor, index) => {
                        const availableVersions = editor.versions?.map((v) => v.version) || [];
                        const hasVersionSupport = availableVersions.length > 0;
                        const selectedVersions = editorVersionRestrictions[editor.id] || [];
                        const hasVersionRestrictions = selectedVersions.length > 0;

                        return (
                            <div key={`${editor.id}-${index}`}>
                                <div className="group flex min-h-[36px] flex-row items-center gap-3">
                                    <Switch
                                        disabled={!editable}
                                        state={selectedEditors[editor.id] ? "checked" : "unchecked"}
                                        onToggle={(checked) => handleEditorSelectionChange(editor.id, checked)}
                                        id={`editor-toggle-${index}`}
                                    />
                                    <div className="flex flex-row items-center gap-2">
                                        <EditorIcon editor={editor} size="base" />
                                        <span>{editor.name}</span>
                                    </div>
                                    <div className="ml-auto flex items-center gap-2">
                                        {defaultEditor === editor.id && (
                                            <Text className="text-content-secondary">default</Text>
                                        )}
                                        {defaultEditor !== editor.id && selectedEditors[editor.id] && (
                                            <span className="relative inline-flex h-7 w-[90px] items-center justify-end">
                                                <Button
                                                    disabled={!editable}
                                                    variant="ghost"
                                                    size="md"
                                                    className="absolute right-0 hidden h-7 border-0 bg-none p-0 text-content-secondary hover:bg-transparent group-hover:block"
                                                    onClick={() => handleDefaultEditorChange(editor.id)}
                                                    data-tracking-id="make-default-manage-editors-modal"
                                                >
                                                    make default
                                                </Button>
                                            </span>
                                        )}
                                        {hasVersionSupport && selectedEditors[editor.id] && (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        disabled={!editable}
                                                        variant="secondary"
                                                        size="md"
                                                        className="font-sm h-7 gap-1 rounded-lg px-2 py-1.5 font-sans text-sm tracking-tight text-content-primary"
                                                        data-tracking-id="add-version-manage-editors-modal"
                                                    >
                                                        add version
                                                        <ChevronDown className="h-3 w-3 opacity-60" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent
                                                    align="end"
                                                    className="min-w-[200px] !pb-0"
                                                    onCloseAutoFocus={(e) => e.preventDefault()}
                                                >
                                                    {availableVersions.length > 0 &&
                                                        availableVersions.map((version, idx) => (
                                                            <Label
                                                                key={version}
                                                                className="relative flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-surface-hover"
                                                                data-tracking-id={`version-checkbox-${editor.alias}-${version}`}
                                                            >
                                                                <Checkbox
                                                                    id={`${editor.alias}-${version}`}
                                                                    checked={selectedVersions.includes(version)}
                                                                    onCheckedChange={(checked) => {
                                                                        const newVersions = checked
                                                                            ? [...selectedVersions, version]
                                                                            : selectedVersions.filter(
                                                                                  (v) => v !== version,
                                                                              );
                                                                        handleVersionRestrictionsChange(
                                                                            editor.id,
                                                                            newVersions,
                                                                        );
                                                                    }}
                                                                />
                                                                {version}
                                                                {idx === 0 && (
                                                                    <span className="ml-auto rounded-md bg-surface-secondary px-2 py-0.5 text-xs text-content-secondary">
                                                                        current latest
                                                                    </span>
                                                                )}
                                                            </Label>
                                                        ))}
                                                    {selectedVersions.length === 0 && (
                                                        <div className="flex items-start gap-2 bg-surface-button-clear-accent px-3 py-2">
                                                            <Info className="mt-0.5 h-4 w-4 shrink-0 text-content-brand" />
                                                            <span className="text-sm text-content-brand">
                                                                No selection means latest version will be used
                                                            </span>
                                                        </div>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}
                                    </div>
                                </div>
                                {hasVersionSupport && hasVersionRestrictions && selectedEditors[editor.id] && (
                                    <div className="ml-12 flex flex-wrap items-center gap-1">
                                        {selectedVersions.map((version) => {
                                            return (
                                                <span
                                                    key={version}
                                                    className="inline-flex h-6 items-center gap-0.5 rounded-[20px] bg-surface-brand px-2 py-1 font-mono text-xs font-normal text-[rgb(var(--ona-blue-500))]"
                                                >
                                                    {version}
                                                    <button
                                                        type="button"
                                                        disabled={!editable}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const newVersions = hasVersionRestrictions
                                                                ? selectedVersions.filter((v) => v !== version)
                                                                : availableVersions.filter((v) => v !== version);
                                                            handleVersionRestrictionsChange(editor.id, newVersions);
                                                        }}
                                                        className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-gray-900 hover:bg-content-brand/20 disabled:cursor-not-allowed disabled:opacity-50"
                                                        data-tracking-id={
                                                            hasVersionRestrictions
                                                                ? "remove-version-tag"
                                                                : "remove-all-version-tag"
                                                        }
                                                        aria-label={`Remove ${version}`}
                                                    >
                                                        <X size={11} />
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </DialogBody>

                <DialogFooter className="items-center">
                    {isInvalidSelection && (
                        <div className="grow">
                            <Text className="text-base text-content-red">You must have 1 available editor</Text>
                        </div>
                    )}
                    <DialogClose asChild>
                        <Button type="button" variant="outline">
                            Close
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleSave}
                        loading={updateOrganizationPolicies.isPending}
                        disabled={!editable || !isDirty || isInvalidSelection}
                        data-testid="save-editors-button"
                        type="button"
                        variant="primary"
                        data-tracking-id="save-editors-manage-editors-modal"
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
